import { useRuntimeConfig } from '#imports'
import { type Component, type MaybeRefOrGetter, computed, toValue } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'
import type { Location } from '@ds/types/Location'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import { CMS } from '~/scopes/cms/routes-names'
import { PARTNERSHIPS } from '~/scopes/partnerships/routes-names'

import translations from './usePlpBlocks.translations'

type I18n = ReturnType<typeof useI18n>

const SMARTPHONE_SLUGS = new Set([
  'smartphones',
  'iphone',
  'samsung',
  'google-pixel',
  'android-smartphones',
])

export type PlpBlockContent = {
  kind: 'warranty' | 'expressReplacement' | 'telcoSingle' | 'telcoMulti'
  variant: 'brandMid' | 'brandLow' | 'defaultMid'
  title: string
  description?: string
  ctaLabel?: string
  ctaUrl?: Location
  icon?: { src: Component | string; width?: number; height?: number }
  iconMobileOnly?: boolean
  image?: { src: string; width: number; height: number }
  trackingName: 'warranty' | 'express_replacement' | 'bouygues' | 'us_telco'
}

export type PlpBlocksData = {
  block1: PlpBlockContent
  block2?: PlpBlockContent
  block3?: PlpBlockContent
}

function getWarranty(i18n: I18n): PlpBlockContent {
  return {
    kind: 'warranty',
    variant: 'brandMid',
    title: i18n(translations.warrantyTitle),
    description: i18n(translations.warrantySubtitle),
    icon: { src: IconQuality },
    trackingName: 'warranty',
  }
}

function getExpressReplacement(i18n: I18n, locale: string): PlpBlockContent {
  return {
    kind: 'expressReplacement',
    variant: 'brandLow',
    title: i18n(translations.expressReplacementTitle),
    description: i18n(translations.expressReplacementSubtitle),
    ctaLabel: i18n(translations.expressReplacementCta),
    ctaUrl: `/${locale}/${CMS.EXPRESS_REPLACEMENT}`,
    icon: { src: IconTruckExpress },
    image: {
      src: '/img/plp/new-express-replacement.webp',
      width: 915,
      height: 1080,
    },
    trackingName: 'express_replacement',
  }
}

function getTelco(
  isSmartphones: boolean,
  countryCode: string,
  i18n: I18n,
  locale: string,
  withLocale: ReturnType<typeof useRouteLocationWithLocale>,
): PlpBlockContent | undefined {
  if (!isSmartphones) return undefined

  if (countryCode === MarketCountryCode.FR) {
    return {
      kind: 'telcoSingle',
      variant: 'defaultMid',
      title: i18n(translations.telcoSingleTitle),
      ctaLabel: i18n(translations.telcoSingleCta),
      ctaUrl: `/${locale}/service/bouygues-telecom`,
      icon: { src: '/img/plp/telco-single-logos.svg', width: 117, height: 42 },
      trackingName: 'bouygues',
    }
  }

  if (countryCode === MarketCountryCode.US) {
    return {
      kind: 'telcoMulti',
      variant: 'brandLow',
      title: i18n(translations.telcoMultiTitle),
      description: i18n(translations.telcoMultiSubtitle),
      ctaLabel: i18n(translations.telcoMultiCta),
      ctaUrl: withLocale({ name: PARTNERSHIPS.US_TELCO_COMPARISON }),
      icon: { src: '/img/plp/telco-multi-logos.svg', width: 198, height: 42 },
      iconMobileOnly: true,
      image: {
        src: '/img/plp/telco-multi-block.webp',
        width: 613,
        height: 625,
      },
      trackingName: 'us_telco',
    }
  }

  return undefined
}

export function usePlpBlocks(slug: MaybeRefOrGetter<string | undefined>) {
  const { FF_ENABLE_SERVICES_REVAMP = '' } = useRuntimeConfig().public
  const { market } = useMarketplace()
  const locale = useI18nLocale().toLowerCase()
  const i18n = useI18n()
  const withLocale = useRouteLocationWithLocale()

  const plpBlocks = computed<PlpBlocksData | undefined>(() => {
    if (!FF_ENABLE_SERVICES_REVAMP.includes(market.countryCode))
      return undefined

    const isSmartphones = SMARTPHONE_SLUGS.has(toValue(slug) ?? '')

    const expressReplacement = getExpressReplacement(i18n, locale)
    const telco = getTelco(
      isSmartphones,
      market.countryCode,
      i18n,
      locale,
      withLocale,
    )

    return {
      block1: getWarranty(i18n),
      block2: expressReplacement ?? telco,
      block3: expressReplacement && telco,
    }
  })

  return { plpBlocks }
}
